import Phaser from "phaser";
import type { PropView } from "daedalus-renderer-phaser";
import { SIXTEENTHS, type RenderDivisor } from "odyssey-shared";
import { RENDER_LABELS } from "../menu/render-choices.ts";
import { gameReport } from "../report.ts";
import { collectMachine, measureRefresh, UNAVAILABLE } from "./machine.ts";
import { mountBenchPanel, type BenchPanel } from "./panel.ts";
import { REPORT_FORMAT, REPORT_VERSION, reportText, type BenchReport } from "./report.ts";
import { parseRoute, type BenchRoute } from "./route.ts";
import stressRouteFile from "./routes/stress.json";
import { BenchRunner } from "./runner.ts";
import { chunkFigures } from "./stats.ts";
import { benchEnvelope, initialLabel, keepLabel, postBenchReport, savedLine, storedLabel, type SaveOutcome } from "./submit.ts";

/**
 * The game page's benchmark mode (P2.0-T4): `?bench=1` runs the route this build carries for the page's world (the
 * density stress world's `routes/stress.json`), and `?bench=<path>` fetches a route file from the page's own origin
 * for any other map. `?label=` fills the panel's label, else the label this browser remembers. `?render=1`, `2`
 * or `3` draws the run at that render resolution as far as the window offers it, without storing it (ADR-093
 * decision 10: the gate compares Full with Half), and the machine block records the divisor in force. The page mounts the
 * panel, the scene takes the runner as its director, and the run starts when Start is pressed once the world shows.
 * The finished report is what "Copy results" copies and what `window.gameBench` holds for a script that drives the
 * page; it is then posted to the dev server's report endpoint (`submit.ts`, P2.0-T8), which only the dev server's
 * `config.json` names, with a plain same-origin request that asks the person nothing (the route needs no editor
 * capability, ADR-050's amendment of 2026-10-04), and the panel says where it was saved or why it was not.
 */

/**
 * The routes this build carries, by world id.
 */
const ROUTES: Readonly<Record<string, unknown>> = { stress: stressRouteFile };

/**
 * What a script driving the page reads: the runner's state, the step running, the report and its text once done, and
 * then how saving it on the dev server ended (null until the post has an answer).
 */
export interface BenchWindow {
  state: string;
  step: string;
  report: BenchReport | null;
  text: string;
  saved: SaveOutcome | null;
}

/**
 * The game scene as the benchmark reads it: its prop views.
 */
interface BenchScene extends Phaser.Scene {
  readonly props: ReadonlyMap<string, PropView>;
}

/**
 * The build's commit, which the bundler substitutes, or "unavailable" for a bundle built without it.
 *
 * @returns The commit.
 */
function buildSha(): string {
  return typeof __BUILD_SHA__ !== "undefined" && __BUILD_SHA__ !== "" ? __BUILD_SHA__ : UNAVAILABLE;
}

/**
 * The render resolution in force as the machine block words it.
 *
 * @param divisor The divisor in force.
 * @returns Such as "Half (divisor 2)".
 */
export function renderWords(divisor: RenderDivisor): string {
  return `${RENDER_LABELS[divisor]} (divisor ${divisor})`;
}

/**
 * The page's `localStorage`, or null in a browser that withholds it.
 *
 * @returns The storage, or null.
 */
function pageStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Reads the route a `bench` parameter names.
 *
 * @param bench The parameter: `1` for the world's built-in route, else a path on the page's origin.
 * @param worldId The page's world.
 * @returns The route.
 * @throws Error naming why when there is no route for the world, the path leaves the origin, or the file does not read.
 */
async function loadRoute(bench: string, worldId: string): Promise<BenchRoute> {
  if (bench === "1") {
    const builtIn = ROUTES[worldId];
    if (builtIn === undefined) {
      throw new Error(`this build carries no benchmark route for ${worldId}; open /game?world=stress&bench=1, or name a route file with ?bench=/path/route.json`);
    }
    return parseRoute(builtIn);
  }
  const url = new URL(bench, window.location.href);
  if (url.origin !== window.location.origin) {
    throw new Error("a route file must be on this page's own origin");
  }
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`the route file ${url.pathname} answered ${response.status}`);
  }
  return parseRoute(await response.json());
}

/**
 * The benchmark of a page whose URL asks for one: the runner the scene takes as its director, and the mount the page
 * calls once its game exists.
 */
export interface PageBench {
  readonly runner: BenchRunner;
  mount(game: Phaser.Game, host: HTMLElement): void;
}

/**
 * Makes the page's benchmark when the URL asks for one.
 *
 * @param params The page's query.
 * @param worldId The page's world.
 * @param mapId The map the page plays.
 * @param reportsEndpoint Where a finished report is posted: the endpoint the page's `config.json` names, or an empty
 *   string, which posts nothing.
 * @param renderDivisor Reads the render divisor in force, which the report records as the run ends.
 * @returns The benchmark, or null without `bench`.
 */
export function benchFromUrl(params: URLSearchParams, worldId: string, mapId: string, reportsEndpoint: string, renderDivisor: () => RenderDivisor = () => 1): PageBench | null {
  const bench = params.get("bench");
  if (bench === null || bench === "" || bench === "0") {
    return null;
  }
  const exposed: BenchWindow = { state: "loading", step: "", report: null, text: "", saved: null };
  (window as unknown as { gameBench: BenchWindow }).gameBench = exposed;
  let scene: BenchScene | null = null;
  let game: Phaser.Game | null = null;
  let panel: BenchPanel | null = null;
  let route: BenchRoute | null = null;
  const refresh = measureRefresh();
  const runner = new BenchRunner(
    {
      tileSize: 32,
      chunkSize: 32,
      now: () => performance.now(),
      cameraCentre: () => scene?.cameras.main.midPoint ?? { x: 0, y: 0 },
      painted: () => gameReport.drawn,
      character: () => ({ x: gameReport.localX / SIXTEENTHS, y: gameReport.localY / SIXTEENTHS }),
      props: () => {
        let count = 0;
        for (const view of scene?.props.values() ?? []) {
          count += view.count;
        }
        return count;
      },
      creatures: () => gameReport.creatures().length,
    },
    (rows, logs) => {
      void finish(rows, logs);
    },
  );

  /**
   * Builds the report once the route ends, shows it, and posts it to the dev server; the panel and `window.gameBench`
   * then say where it was saved or why it was not.
   *
   * @param rows The rows' figures.
   * @param logs The rows' logs, for the per-chunk figures.
   */
  async function finish(rows: Parameters<ConstructorParameters<typeof BenchRunner>[1]>[0], logs: Parameters<ConstructorParameters<typeof BenchRunner>[1]>[1]): Promise<void> {
    const renderer = game?.renderer as { gl?: WebGLRenderingContext } | undefined;
    const label = panel?.label() ?? "";
    const machine = await collectMachine(
      { gl: renderer?.gl ?? null, canvas: game?.canvas ?? null, phaserVersion: Phaser.VERSION, build: buildSha(), world: worldId, map: mapId, route: route?.format ?? 0, label, render: renderWords(renderDivisor()) },
      await refresh,
    );
    const live = route as BenchRoute;
    const report: BenchReport = { format: REPORT_FORMAT, version: REPORT_VERSION, machine, route: { id: live.id, format: live.format, world: live.world, map: live.map }, rows, chunks: chunkFigures(logs) };
    const text = reportText(report);
    exposed.report = report;
    exposed.text = text;
    exposed.state = "done";
    panel?.showResults(report, text);
    keepLabel(pageStorage(), label);
    const envelope = benchEnvelope({ when: new Date(), search: params.size === 0 ? "" : `?${params.toString()}`, userAgent: navigator.userAgent, screenWidth: screen.width, screenHeight: screen.height, devicePixelRatio: window.devicePixelRatio }, machine, label);
    const outcome = await postBenchReport((input, init) => fetch(input, init), reportsEndpoint, { envelope, text });
    exposed.saved = outcome;
    panel?.showSaved(savedLine(outcome, label));
  }

  return {
    runner,
    mount(started, host) {
      game = started;
      const tick = (): void => {
        scene ??= (started.scene.getScene("game") as BenchScene | null) ?? null;
        runner.frame();
        exposed.step = runner.stepId;
        if (runner.state === "running") {
          panel?.setStatus(`Running ${runner.stepId}. Leave the tab in front.`);
        } else if (runner.state === "idle") {
          panel?.setReady(gameReport.ready && route !== null);
        }
      };
      started.events.on(Phaser.Core.Events.POST_RENDER, tick);
      loadRoute(bench, worldId).then(
        (loaded) => {
          if (loaded.world !== worldId || loaded.map !== mapId) {
            panel = mountBenchPanel(host, "", { error: `route ${loaded.id} runs on ${loaded.world}/${loaded.map}, not on this page's ${worldId}/${mapId}` }, () => undefined);
            exposed.state = "refused";
            return;
          }
          route = loaded;
          exposed.state = "idle";
          panel = mountBenchPanel(host, initialLabel(params.get("label"), storedLabel(pageStorage())), { id: loaded.id }, () => {
            exposed.state = "running";
            runner.start(loaded);
          });
        },
        (error: unknown) => {
          exposed.state = "refused";
          panel = mountBenchPanel(host, "", { error: error instanceof Error ? error.message : String(error) }, () => undefined);
        },
      );
    },
  };
}
